/**
 * Advisor Metadata Utilities
 *
 * Standardizes the metadata fields used during advisor signup.
 * Ensures consistent field names between signup and profile creation.
 */

/**
 * Standard field names for advisor user metadata
 * These must match what's read back in email-confirmed and auth/callback
 */
export const ADVISOR_METADATA_FIELDS = {
  IS_ADVISOR: 'is_advisor',
  ACCOUNT_TYPE: 'account_type',
  FULL_NAME: 'full_name',
  PROFESSION_TYPE: 'profession_type',
  FIRM_NAME: 'firm_name',
  LICENSE_NUMBER: 'license_number',
  LICENSE_STATE: 'license_state',
} as const;

/**
 * Typed advisor metadata structure
 */
export interface AdvisorMetadata {
  is_advisor: true;
  account_type: 'advisor';
  full_name?: string;
  profession_type?: string;
  firm_name?: string | null;
  license_number?: string | null;
  license_state?: string | null;
}

/**
 * Build consistent advisor metadata for Supabase auth signup
 *
 * @param data - Advisor details from signup form
 * @returns Metadata object to pass to supabase.auth.signUp({ data: ... })
 */
export function buildAdvisorMetadata(data: {
  fullName?: string;
  professionType?: string;
  firmName?: string;
  licenseNumber?: string;
  licenseState?: string;
}): AdvisorMetadata {
  return {
    // Required advisor flags
    is_advisor: true,
    account_type: 'advisor',
    // Optional fields - use undefined for empty strings to avoid storing empty values
    full_name: data.fullName || undefined,
    profession_type: data.professionType || undefined,
    firm_name: data.firmName || null,
    license_number: data.licenseNumber || null,
    license_state: data.licenseState || null,
  };
}

/**
 * Check if user metadata indicates an advisor account
 */
export function isAdvisorMetadata(metadata: Record<string, unknown> | undefined | null): boolean {
  if (!metadata) return false;
  return metadata.is_advisor === true || metadata.account_type === 'advisor';
}

/**
 * Extract advisor fields from user metadata
 * Used when creating profile/advisor_profiles from metadata
 */
export function extractAdvisorFields(metadata: Record<string, unknown> | undefined | null): {
  fullName: string;
  professionType: string;
  firmName: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
} {
  if (!metadata) {
    return {
      fullName: '',
      professionType: 'other',
      firmName: null,
      licenseNumber: null,
      licenseState: null,
    };
  }

  return {
    fullName: (metadata.full_name as string) || '',
    professionType: (metadata.profession_type as string) || 'other',
    firmName: (metadata.firm_name as string) || null,
    licenseNumber: (metadata.license_number as string) || null,
    licenseState: (metadata.license_state as string) || null,
  };
}
